/// <reference path="../models/pendo.d.ts" />
import { inject, Injectable } from '@angular/core';
import { HashTools, NavigationService, RouterTools } from '@sol/components';
import { AsmRouteId } from '@asm/components-core/models/asm-route-id';
import { AsmComponentsService } from '@asm/components-core/services/asm-components.service';
import { SessionService, SessionState } from '@asm/components-platform-context/services/session.service';

@Injectable({ providedIn: 'root' })
export class PendoService {
  private static readonly CDN_URL = 'https://cdn.eu.pendo.io/agent/static/';

  private readonly asmComponentsService = inject(AsmComponentsService);
  private readonly navigationService = inject(NavigationService);
  private readonly sessionService = inject(SessionService);
  private agentLoaded = false;
  private agentReady = false;
  private active = false;
  private bound = false;
  private pendingInit: { userId: string; accountId: string } | undefined;

  constructor() {
    // watchComputed must be called in the constructor (injection context)
    this.sessionService.watchComputed(
      state => [state.appId, state.pendoOptIn, state.userId, state.currentPartnerId, state.scope?.scope],
      () => {
        if (!this.bound) {
          return;
        }
        const apiKey = this.asmComponentsService.state.pendoApiKey;
        const state = this.sessionService.state;
        // Derive opt-in from partner/scope data directly to avoid stale pendoOptIn during transitions
        if (PendoService.resolveOptIn(state)) {
          this.start(apiKey, state.userId, PendoService.resolveAccountId(state));
        } else {
          this.stop();
        }
      },
    );
  }

  // Call once from AppComponent.ngOnInit after session fetch
  public bind(): void {
    const env = this.asmComponentsService.state.environment;
    const apiKey = this.asmComponentsService.state.pendoApiKey;
    if (!['dit', 'uat', 'prod'].includes(env) || !apiKey) {
      return;
    }

    this.bound = true;

    const state = this.sessionService.state;
    if (PendoService.resolveOptIn(state)) {
      this.start(apiKey, state.userId, PendoService.resolveAccountId(state));
    }
  }

  private start(apiKey: string, userId: string, accountId: string): void {
    if (!this.agentLoaded) {
      this.loadAgent(apiKey);
      this.agentLoaded = true;
    }

    // Agent still loading from CDN — defer initialization
    if (!this.agentReady) {
      this.pendingInit = { userId, accountId };
      return;
    }

    this.doInitialize(userId, accountId);
  }

  private doInitialize(userId: string, accountId: string): void {
    try {
      const anonVisitor = HashTools.anonymize(userId);
      const anonAccount = HashTools.anonymize(accountId);
      if (this.active) {
        window.pendo?.identify({
          visitor: { id: anonVisitor },
          account: { id: anonAccount },
        });
      } else {
        window.pendo?.initialize({
          excludeAllText: true,
          preventCodeInjection: true,
          location: {
            transforms: [{ attr: 'search', action: 'Clear' }],
          },
          sanitizeUrl: (url: string) => RouterTools.sanitizeUrl(url, this.navigationService.state.activeRoute?.id),
          visitor: { id: anonVisitor },
          account: { id: anonAccount },
          events: {
            guidesLoaded: () => window.pendo?.startGuides(),
          },
        });
        this.active = true;
      }
    } catch (error) {
      console.error('Pendo initialization failed:', error);
    }
  }

  private stop(): void {
    // Clear pending initialization to prevent tracking after CDN script loads
    this.pendingInit = undefined;

    if (!this.active) {
      return;
    }
    try {
      window.pendo?.teardown();
    } catch (error) {
      console.error('Pendo teardown failed:', error);
    }
    this.active = false;
  }

  private static resolveOptIn(state: SessionState): boolean {
    // Never track in admin interface
    if (state.appId === AsmRouteId.app_admin) return false;
    if (state.appId === AsmRouteId.app_partner) {
      if (!state.currentPartnerId) return false;
      return state.partners.find(p => p.id === state.currentPartnerId)?.pendoOptIn ?? false;
    }
    return state.scopes.find(s => s.name === state.scope?.scope)?.pendoOptIn ?? false;
  }

  private static resolveAccountId(state: SessionState): string {
    return state.appId === AsmRouteId.app_partner ? (state.currentPartnerId ?? '') : (state.scope?.scope ?? '');
  }

  private loadAgent(apiKey: string): void {
    const script = document.createElement('script');
    script.async = true;
    script.src = `${PendoService.CDN_URL}${apiKey}/pendo.js`;
    script.onload = () => {
      this.agentReady = true;
      if (this.pendingInit) {
        const { userId, accountId } = this.pendingInit;
        this.pendingInit = undefined;
        this.doInitialize(userId, accountId);
      }
    };
    script.onerror = () => {
      console.warn('Pendo agent failed to load');
      this.pendingInit = undefined;
    };
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }
  }
}
